import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Toast, Modal } from "@/components/Layout";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || "Eroare";
const fmt = (d) => (d ? d.split("-").reverse().join(".") : "");

/** Anii universitari ai modulului și parametrii lor (coeficienți, norme minime, formațiuni). */
export default function Years() {
  const [years, setYears] = useState(null);
  const [creating, setCreating] = useState(null);   // { name, start_date, end_date, copy_from_id }
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3000); };

  const load = () => api.get("/stat-didactic/years").then(r => setYears(r.data)).catch(e => { setYears([]); toast(errText(e), "error"); });
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const last = years && years[0];
    const a = last ? parseInt(last.name.slice(0, 4), 10) + 1 : new Date().getFullYear();
    setCreating({ name: `${a}-${a + 1}`, start_date: `${a}-09-28`, end_date: `${a + 1}-09-26`, copy_from_id: last ? last.id : "" });
  };
  const create = async () => {
    try {
      await api.post("/stat-didactic/years", { ...creating, copy_from_id: creating.copy_from_id || null });
      setCreating(null); toast("An universitar creat", "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const setCurrent = async (y) => {
    try { await api.post(`/stat-didactic/years/${y.id}/set-current`); toast(`${y.name} este anul curent`, "success"); load(); }
    catch (e) { toast(errText(e), "error"); }
  };
  const remove = async () => {
    try { await api.delete(`/stat-didactic/years/${deleting.id}`); setDeleting(null); toast("An șters", "success"); load(); }
    catch (e) { toast(errText(e), "error"); setDeleting(null); }
  };

  if (years === null) return <div>Se încarcă...</div>;
  return (
    <>
      <PageHeader title="Ani universitari" subtitle="Fiecare an are propriii parametri: coeficienți de transformare, norme minime, dimensiuni de formațiuni"
        actions={<button className="btn btn-primary" onClick={openNew} data-testid="sfd-year-new">+ An universitar nou</button>} />
      <Toast msg={msg.text} type={msg.type} />
      <div className="card">
        <table className="tbl">
          <thead><tr><th>An universitar</th><th>Perioadă</th><th>Săptămâni</th><th>Ore anuale (fișa postului)</th><th></th></tr></thead>
          <tbody>{years.map(y => (
            <tr key={y.id} data-testid={`sfd-year-${y.name}`}>
              <td style={{ fontWeight: 700 }}>{y.name} {y.is_current && <span className="badge badge-active" style={{ marginLeft: 8 }}>An curent</span>}</td>
              <td>{fmt(y.start_date)} – {fmt(y.end_date)}</td>
              <td>{y.weeks_per_year} ({y.weeks_per_semester} pe semestru)</td>
              <td>{y.annual_hours}</td>
              <td style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Link className="btn btn-primary" style={{ padding: "3px 10px", fontSize: 12 }} to={`/stat-didactic/ani/${y.id}`}>Parametri</Link>
                {!y.is_current && <button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => setCurrent(y)}>Marchează ca an curent</button>}
                <button className="btn btn-danger" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => setDeleting(y)}>Șterge</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {years.length === 0 && <div style={{ padding: 16, color: "#94A3B8", fontSize: 13 }}>Niciun an universitar. Creează primul an: pornește cu valorile din regulament, pe care le poți modifica apoi.</div>}
      </div>

      {creating && (
        <Modal title="An universitar nou" onClose={() => setCreating(null)} width={560}>
          <div className="grid-2">
            <div><label className="label label-req">Denumire (ex. 2027-2028)</label>
              <input className="input" value={creating.name} onChange={e => setCreating({ ...creating, name: e.target.value })} data-testid="sfd-year-name" /></div>
            <div><label className="label">Pornește de la</label>
              <select className="select" value={creating.copy_from_id} onChange={e => setCreating({ ...creating, copy_from_id: e.target.value })} data-testid="sfd-year-copy">
                <option value="">Valorile din regulament</option>
                {years.map(y => <option key={y.id} value={y.id}>Copie a anului {y.name}</option>)}
              </select></div>
            <div><label className="label label-req">Început</label>
              <input className="input" type="date" value={creating.start_date} onChange={e => setCreating({ ...creating, start_date: e.target.value })} /></div>
            <div><label className="label label-req">Sfârșit</label>
              <input className="input" type="date" value={creating.end_date} onChange={e => setCreating({ ...creating, end_date: e.target.value })} /></div>
          </div>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 10 }}>
            O copie păstrează toți parametrii anului ales (inclusiv modificările făcute de tine), dar anul nou se editează independent.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setCreating(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={create} data-testid="sfd-year-save">Creează</button>
          </div>
        </Modal>
      )}
      {deleting && (
        <Modal title={`Șterge anul ${deleting.name}?`} onClose={() => setDeleting(null)} width={460}>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 16 }}>Se șterg toți parametrii acestui an (coeficienți, norme, dimensiuni). Acțiunea nu poate fi anulată.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={remove} data-testid="sfd-year-delete-confirm">Șterge</button>
          </div>
        </Modal>
      )}
    </>
  );
}
